import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { map, Observable, Subject, of } from "rxjs";

import { Agent } from "@models/agent.interface";
import { StorageArea, PaginatedResponse, GiftProductResponse as GiftProductsResponse } from "@models/agents-dtos.model";
import { ApiResponse } from "@models/base-response.model";

import { environment } from "../../environments/environment";
import {
  Discount,
  Catalog,
  PaginatedProducts,
  ProductsParams,
  ProductCustomerDiscountsParams,
  AddMultipleDiscountsRequest,
  EditPointsRequest,
  ProductsResponse,
  DiscountProductResponse,
  DiscountProduct,
  UpdateCustomerDiscountRequest,
} from "../api/dto/products.dto";
import { ExportDiscountedProductsParams } from "../api/requests/products.requests";



@Injectable({
  providedIn: "root",
})
export class ProductsService {
  private headersApp_Json: HttpHeaders;
  subjectNotifier: Subject<void> = new Subject<void>();
  productsVersion = signal(0);

  shouldFetchStorages = signal(false);
  shouldFetchCatalogs = signal(false);

  storagesResource = rxResource<StorageArea[], number | null>({
    params: () => (this.shouldFetchStorages() ? this.productsVersion() : null),
    stream: (ctx) => {
      if (ctx.params === null) return of([]);
      return this.getStorages().pipe(map(
        res => (
          res.data?.list || [])));
    },
  });

  catalogsResource = rxResource<Catalog[], number | null>({
    params: () => (this.shouldFetchCatalogs() ? this.productsVersion() : null),
    stream: (ctx) => {
      if (ctx.params === null) return of([]);
      return this.getCatalogs().pipe(map(res => (res.data?.list || [])));
    },
  });

  constructor(private http: HttpClient) {
    this.headersApp_Json = new HttpHeaders().set(
      "Content-Type",
      "application/json",
    );
  }

  /** Clears lazy-fetch flags so rxResources leave any error/stale state. */
  resetSessionState(): void {
    this.shouldFetchStorages.set(false);
    this.shouldFetchCatalogs.set(false);
  }

  notifyAboutChange() {
    this.subjectNotifier.next();
    this.productsVersion.update((v) => v + 1);
  }

  getAllProducts(filterObj: Record<string, string | number | boolean | readonly (string | number | boolean)[]>, skipLoader: boolean = false): Observable<PaginatedProducts> {
    const params = new HttpParams({ fromObject: { ...filterObj, skipLoader } });
    return this.http.get<ProductsResponse>(environment.baseUrl + "Product", { params }).pipe(
      map((response) => ({
        items: response.data?.list || [],
        total: response.data?.totalCount || 0,
        isPurseEmpty: response.data?.isPurseEmpty ?? false,
      })),
    );
  }

  getAllProductsPaginated(params: ProductsParams): Observable<PaginatedProducts> {
    let httpParams = new HttpParams()
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString())
      .set("skipLoader", params.skipLoader ?? false);

    if (params.filter !== undefined) {
      httpParams = httpParams.set("filter", params.filter ?? "");
    }

    if (params.discountFilter !== undefined) {
      httpParams = httpParams.set("discountFilter", String(params.discountFilter));
    }

    if (params.customerDiscountFilter !== undefined) {
      httpParams = httpParams.set("CustomerDiscountFilter", String(params.customerDiscountFilter));
    }

    if (params.storageAreaCode != null) {
      httpParams = httpParams.set("storageAreaCode", params.storageAreaCode.toString());
    }

    if (params.idAreaManager != null) {
      httpParams = httpParams.set("idAreaManager", params.idAreaManager.toString());
    }

    if (params.catalogCode != null) {
      httpParams = httpParams.set("catalogCode", params.catalogCode.toString());
    }

    if (params.id_listino != null) {
      httpParams = httpParams.set("id_listino", params.id_listino.toString());
    }

    if (params.idCustomer != null) {
      httpParams = httpParams.set("idCustomer", params.idCustomer.toString());
    }

    if (params.promotion !== undefined) {
      httpParams = httpParams.set("promotion", String(params.promotion));
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      params.categoryIds.forEach((id) => {
        httpParams = httpParams.append("categoryIds", id.toString());
      });
    }

    return this.http
      .get<ProductsResponse>(environment.baseUrl + "Product", { params: httpParams })
      .pipe(
        map((res) => ({
          items: res?.data?.list ?? [],
          total: res?.data?.totalCount ?? 0,
          isPurseEmpty: res?.data?.isPurseEmpty ?? false,
        })),
      );
  }

  getGiftProducts(
    agentId: number,
    page = 1,
    pageResults = 10000,
  ): Observable<GiftProductsResponse> {
    const url = `${environment.baseUrl}GiftProduct?agentId=${agentId}&page=${page}&pageResults=${pageResults}`;
    return this.http.get<GiftProductsResponse>(url);
  }

  addCustomersProdDiscounts(body: AddMultipleDiscountsRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      environment.baseUrl + "CustomerPersonalizedProduct/addMultipleDiscounts",
      body
    );
  }

  addProdDiscount(
    idProduct: number,
    discount: number,
    discountStartValidity: string,
    discountEndValidity: string,
  ): Observable<ApiResponse<void>> {
    const params = new HttpParams()
      .set("idProduct", idProduct.toString())
      .set("discount", discount.toString())
      .set("discountStartValidity", discountStartValidity)
      .set("discountEndValidity", discountEndValidity);
    return this.http.post<ApiResponse<void>>(
      environment.baseUrl +
      "CustomerPersonalizedProduct/AddMaxiveDiscountProduct?" +
      params,
      null
    );
  }

  modifyDiscountProduct(id: number, request: Partial<Discount> & { discountPercentage?: number }): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      environment.baseUrl +
      "CustomerPersonalizedProduct/discount-products/" +
      id,
      request
    );
  }

  getProductsExcel(body: object): Observable<string> {
    return this.http.post(environment.baseUrl + "Product/DownloadCsv", body, {
      responseType: "text",
    });
  }

  getDiscountedProductsExcel(
    params: ExportDiscountedProductsParams,
  ): Observable<HttpResponse<ArrayBuffer>> {
    const toParam = (value: number | null): string =>
      value == null ? "null" : String(value);

    const httpParams = new HttpParams()
      .set("customerId", toParam(params.customerId))
      .set("storageAreaId", toParam(params.storageAreaId))
      .set("id_listino", toParam(params.id_listino));

    return this.http.get(
      environment.baseUrl + "Product/ExportDiscountedProducts",
      {
        params: httpParams,
        responseType: "arraybuffer",
        observe: "response",
      },
    );
  }

  getDiscountHistory(
    idProduct: number,
    idAreaManager?: number,
    customerId?: number,
    skipLoader: boolean = false,
  ): Observable<DiscountProduct[]> {

    let params = new HttpParams()
      .set("idProduct", idProduct.toString())
      .set("skipLoader", skipLoader);

    if (idAreaManager !== undefined) {
      params = params.set("idAreaManager", idAreaManager.toString());
    }

    if (customerId) {
      params = params.set("idCustomer", customerId.toString());
    }

    return this.http.get<DiscountProductResponse>(
      environment.baseUrl + "DiscountProduct",
      { params }
    ).pipe(
      map((res) => res.data.list)
    );
  }

  getProductCustomerDiscounts(
    params: ProductCustomerDiscountsParams,
  ): Observable<{ items: DiscountProduct[]; total: number }> {
    let httpParams = new HttpParams()
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString())
      .set("skipLoader", String(params.skipLoader ?? false));

    if (params.filter?.trim()) {
      httpParams = httpParams.set("filter", params.filter.trim());
    }
    if (params.validityFrom?.trim()) {
      httpParams = httpParams.set("validityFrom", params.validityFrom.trim());
    }
    if (params.validityTo?.trim()) {
      httpParams = httpParams.set("validityTo", params.validityTo.trim());
    }

    return this.http
      .get<DiscountProductResponse>(
        `${environment.baseUrl}Product/${params.idProduct}/Discounted-customers`,
        { params: httpParams },
      )
      .pipe(
        map((res) => ({
          items: res?.data?.list ?? [],
          total: res?.data?.totalCount ?? 0,
        })),
      );
  }

  updateCustomerDiscount(
    id: number,
    body: UpdateCustomerDiscountRequest,
    skipLoader: boolean = false,
  ): Observable<ApiResponse<void>> {
    const params = new HttpParams().set("skipLoader", skipLoader);
    return this.http.put<ApiResponse<void>>(
      `${environment.baseUrl}CustomerPersonalizedProduct/customer-discounts/${id}`,
      body,
      { params },
    );
  }

  getStorages(): Observable<ApiResponse<PaginatedResponse<StorageArea>>> {
    return this.http.get<ApiResponse<PaginatedResponse<StorageArea>>>(
      environment.baseUrl + "GenericLists/StorageArea?page=1&pageResults=20000"
    );
  }


  deleteDiscount(discountId: number, skipLoader: boolean = false): Observable<ApiResponse<void>> {
    const params = new HttpParams().set("skipLoader", skipLoader);
    return this.http.delete<ApiResponse<void>>(
      environment.baseUrl + "CustomerPersonalizedProduct/?id=" + discountId,
      { params },
    );
  }

  deleteGenericDiscount(discountId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      environment.baseUrl +
      "CustomerPersonalizedProduct/DeleteGenericPersonalizedProduct/" +
      "?id=" +
      discountId
    );
  }

  editPoints(id: number, request: EditPointsRequest): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      environment.baseUrl + "Product/Points/" + id,
      request
    );
  }

  getAgentsList(): Observable<ApiResponse<PaginatedResponse<Agent>>> {
    return this.http.get<ApiResponse<PaginatedResponse<Agent>>>(
      environment.baseUrl +
      "Agent/GetAgentsByFilters?areaManager=true&page=1&pageResults=20000"
    );
  }

  getCatalogs(): Observable<ApiResponse<{ list: Catalog[] }>> {
    return this.http.get<ApiResponse<{ list: Catalog[] }>>(
      environment.baseUrl + "Catalog?page=1&pageResults=200000"
    );
  }

  getCapoAreaAgentsList(
    idAreaManager: number,
    productId: number,
  ): Observable<ApiResponse<unknown>> {
    const url = `${environment.baseUrl}GiftProduct/DistributedGiftProduct?areaManagerId=${idAreaManager}&productId=${productId}`;
    return this.http.get<ApiResponse<unknown>>(url);
  }
}
